import { Activity, Position, ActivityEvent, TrackPoint } from "../types";
import debugx from "debugx";
import { getOPFSRoot, OPFSDirectoryHandle } from "../utils/opfs-wrapper";

const debug = debugx("bikedash:storage");

// Re-export TrackPoint for backwards compatibility
export type { TrackPoint } from "../types";

class ActivityStorageService {
  private static instance: ActivityStorageService;
  private rootDirHandle: OPFSDirectoryHandle | null = null;
  private initialized = false;

  static getInstance(): ActivityStorageService {
    if (!ActivityStorageService.instance) {
      ActivityStorageService.instance = new ActivityStorageService();
    }
    return ActivityStorageService.instance;
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      // Request persistent storage
      if (navigator.storage && navigator.storage.persist) {
        const isPersisted = await navigator.storage.persist();
        debug("Storage persisted:", isPersisted);
      }

      // Get OPFS root
      this.rootDirHandle = await getOPFSRoot();

      // Create activities directory if it doesn't exist
      await this.rootDirHandle.getDirectoryHandle("activities", {
        create: true,
      });

      this.initialized = true;
      debug("OPFS storage initialized");
    } catch (error) {
      debug.error("Failed to initialize OPFS storage:", error);
      throw error;
    }
  }

  async saveActivity(activity: Activity): Promise<void> {
    await this.initialize();
    if (!this.rootDirHandle) throw new Error("Storage not initialized");

    try {
      const activitiesDir = await this.rootDirHandle.getDirectoryHandle(
        "activities",
        { create: true },
      );
      const activityDir = await activitiesDir.getDirectoryHandle(activity.id, {
        create: true,
      });

      // Save activity metadata
      const metadataFile = await activityDir.getFileHandle("metadata.json", {
        create: true,
      });
      const metadataWritable = await metadataFile.createWritable();
      await metadataWritable.write(JSON.stringify(activity, null, 2));
      await metadataWritable.close();

      debug("Saved activity:", activity.id);
    } catch (error) {
      debug.error("Failed to save activity:", error);
      throw error;
    }
  }

  async appendEvent(activityId: string, event: ActivityEvent): Promise<void> {
    await this.initialize();
    if (!this.rootDirHandle) throw new Error("Storage not initialized");

    try {
      const activitiesDir =
        await this.rootDirHandle.getDirectoryHandle("activities");
      const activityDir = await activitiesDir.getDirectoryHandle(activityId);

      // Append to events file in JSONL format
      const eventsFile = await activityDir.getFileHandle("events.jsonl", {
        create: true,
      });
      const eventsWritable = await eventsFile.createWritable({
        keepExistingData: true,
      });
      const file = await eventsFile.getFile();
      await eventsWritable.seek(file.size);
      await eventsWritable.write(JSON.stringify(event) + "\n");
      await eventsWritable.close();

      debug("Appended event to activity %s:", activityId, event.type);
    } catch (error) {
      debug.error("Failed to append event:", error);
    }
  }

  async appendTrackPoint(
    activityId: string,
    position: Position,
  ): Promise<void> {
    await this.initialize();
    if (!this.rootDirHandle) throw new Error("Storage not initialized");

    try {
      const activitiesDir =
        await this.rootDirHandle.getDirectoryHandle("activities");
      const activityDir = await activitiesDir.getDirectoryHandle(activityId);

      // Append to track points file in JSONL format
      const trackFile = await activityDir.getFileHandle("track.jsonl", {
        create: true,
      });
      const trackWritable = await trackFile.createWritable({
        keepExistingData: true,
      });
      const file = await trackFile.getFile();
      await trackWritable.seek(file.size);

      const trackPoint: TrackPoint = {
        timestamp: Date.now(),
        position,
      };

      await trackWritable.write(JSON.stringify(trackPoint) + "\n");
      await trackWritable.close();
    } catch (error) {
      debug.error("Failed to append track point:", error);
    }
  }

  async loadActivity(activityId: string): Promise<Activity | null> {
    await this.initialize();
    if (!this.rootDirHandle) throw new Error("Storage not initialized");

    try {
      const activitiesDir =
        await this.rootDirHandle.getDirectoryHandle("activities");
      const activityDir = await activitiesDir.getDirectoryHandle(activityId);

      // Load metadata
      const metadataFile = await activityDir.getFileHandle("metadata.json");
      const metadataContent = await metadataFile.getFile();
      const activity = JSON.parse(await metadataContent.text()) as Activity;

      return activity;
    } catch (error) {
      debug.error("Failed to load activity:", activityId, error);
      return null;
    }
  }

  async listActivities(): Promise<Activity[]> {
    await this.initialize();
    if (!this.rootDirHandle) throw new Error("Storage not initialized");

    const activities: Activity[] = [];

    try {
      const activitiesDir =
        await this.rootDirHandle.getDirectoryHandle("activities");

      // Iterate through all activity directories
      // @ts-ignore - TypeScript types for OPFS are incomplete
      for await (const entry of activitiesDir.values()) {
        if (entry.kind === "directory") {
          const activity = await this.loadActivity(entry.name);
          if (activity) {
            activities.push(activity);
          }
        }
      }

      // Sort by start time (newest first)
      activities.sort((a, b) => (b.startTime || 0) - (a.startTime || 0));

      debug("Loaded %d activities", activities.length);
      return activities;
    } catch (error) {
      debug.error("Failed to list activities:", error);
      return [];
    }
  }

  async deleteActivity(activityId: string): Promise<void> {
    await this.initialize();
    if (!this.rootDirHandle) throw new Error("Storage not initialized");

    try {
      const activitiesDir =
        await this.rootDirHandle.getDirectoryHandle("activities");
      await activitiesDir.removeEntry(activityId, { recursive: true });

      debug("Deleted activity:", activityId);
    } catch (error) {
      debug.error("Failed to delete activity:", error);
      throw error;
    }
  }

  async exportAsGPX(activityId: string): Promise<string> {
    await this.initialize();
    if (!this.rootDirHandle) throw new Error("Storage not initialized");

    try {
      const activity = await this.loadActivity(activityId);
      if (!activity) throw new Error("Activity not found");

      const activitiesDir =
        await this.rootDirHandle.getDirectoryHandle("activities");
      const activityDir = await activitiesDir.getDirectoryHandle(activityId);

      // Load track points
      const trackPoints: TrackPoint[] = [];
      try {
        const trackFile = await activityDir.getFileHandle("track.jsonl");
        const trackContent = await trackFile.getFile();
        const lines = (await trackContent.text())
          .split("\n")
          .filter((line) => line.trim());

        for (const line of lines) {
          try {
            const point = JSON.parse(line) as TrackPoint;
            trackPoints.push(point);
          } catch (e) {
            debug.warn("Failed to parse track point:", e);
          }
        }
      } catch (error) {
        debug.warn("No track points found for activity:", activityId);
      }

      // Generate GPX
      const gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>${activity.title || "Bike Activity"}</name>
    <time>${new Date(activity.startTime).toISOString()}</time>
  </metadata>
  <trk>
    <name>${activity.title || "Bike Activity"}</name>
    <trkseg>
${trackPoints
  .map(
    (
      point,
    ) => `      <trkpt lat="${point.position.lat}" lon="${point.position.lng}">
        <time>${new Date(point.timestamp).toISOString()}</time>
        ${point.position.altitude ? `<ele>${point.position.altitude}</ele>` : ""}
      </trkpt>`,
  )
  .join("\n")}
    </trkseg>
  </trk>
</gpx>`;

      return gpx;
    } catch (error) {
      debug.error("Failed to export GPX:", error);
      throw error;
    }
  }

  async loadTrackPoints(activityId: string): Promise<TrackPoint[]> {
    await this.initialize();
    if (!this.rootDirHandle) throw new Error("Storage not initialized");

    try {
      const activitiesDir =
        await this.rootDirHandle.getDirectoryHandle("activities");
      const activityDir = await activitiesDir.getDirectoryHandle(activityId);

      const trackPoints: TrackPoint[] = [];
      try {
        const trackFile = await activityDir.getFileHandle("track.jsonl");
        const trackContent = await trackFile.getFile();
        const lines = (await trackContent.text())
          .split("\n")
          .filter((line) => line.trim());

        for (const line of lines) {
          try {
            const point = JSON.parse(line) as TrackPoint;
            trackPoints.push(point);
          } catch (e) {
            debug.warn("Failed to parse track point:", e);
          }
        }
      } catch (error) {
        debug.warn("No track points found for activity:", activityId);
      }

      return trackPoints;
    } catch (error) {
      debug.error("Failed to load track points:", error);
      return [];
    }
  }

  async clearAll(): Promise<void> {
    await this.initialize();
    if (!this.rootDirHandle) throw new Error("Storage not initialized");

    try {
      await this.rootDirHandle.removeEntry("activities", { recursive: true });
      await this.rootDirHandle.getDirectoryHandle("activities", {
        create: true,
      });
      debug("Cleared all activities");
    } catch (error) {
      debug.error("Failed to clear activities:", error);
      throw error;
    }
  }

  async saveTrackPoints(
    activityId: string,
    trackPoints: TrackPoint[],
  ): Promise<void> {
    await this.initialize();
    if (!this.rootDirHandle) throw new Error("Storage not initialized");

    try {
      const activitiesDir =
        await this.rootDirHandle.getDirectoryHandle("activities");
      const activityDir = await activitiesDir.getDirectoryHandle(activityId);

      // Write all track points to file (overwrites existing)
      const trackFile = await activityDir.getFileHandle("track.jsonl", {
        create: true,
      });
      const trackWritable = await trackFile.createWritable();

      for (const point of trackPoints) {
        await trackWritable.write(JSON.stringify(point) + "\n");
      }

      await trackWritable.close();
      debug(
        "Saved %d track points for activity %s",
        trackPoints.length,
        activityId,
      );
    } catch (error) {
      debug.error("Failed to save track points:", error);
      throw error;
    }
  }
}

export function getActivityStorageService(): ActivityStorageService {
  return ActivityStorageService.getInstance();
}
